/**
 * The page, with the parser's boxes drawn on it, next to the tree it built.
 *
 * This is the view that answers the question the tool exists for: not "what
 * text came out" but "did it understand the page". A certificate whose text
 * extracts perfectly while every field lands in the wrong table cell is a
 * failure that only a picture shows.
 *
 * The two conversions live in lib/pdflab.ts and are the whole trick: the boxes
 * are in PDF POINTS, and PDF measures from the BOTTOM left, while the rendered
 * image is 200 DPI pixels measured from the top.
 */

import { useMemo, useState } from "react";
import { Card, PillBadge, Select } from "@/components/ui";
import { BADGE_TONE } from "@/components/ui/pillTones";
import { cn } from "@/lib/utils";
import { bboxToPercent, nodeColor, type FlatNode } from "@/lib/pdflab";
import type { RenderedPage } from "@/lib/pdf";

export function StructureView({
  nodes,
  page,
  pageNumber,
  pageCount,
  onPageChange,
}: {
  nodes: FlatNode[];
  page: RenderedPage | null;
  pageNumber: number;
  pageCount: number;
  onPageChange: (n: number) => void;
}) {
  const [hovered, setHovered] = useState<number | null>(null);
  const [typeFilter, setTypeFilter] = useState("");

  const onThisPage = useMemo(
    () => nodes.filter((n) => n.page === pageNumber && n.bbox),
    [nodes, pageNumber],
  );

  const types = useMemo(() => {
    const counts = new Map<string, number>();
    for (const n of onThisPage) counts.set(n.type, (counts.get(n.type) ?? 0) + 1);
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [onThisPage]);

  const shown = typeFilter ? onThisPage.filter((n) => n.type === typeFilter) : onThisPage;

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-fh-border-soft px-4 py-2.5">
          <div className="flex items-center gap-2">
            <span className="text-fh-row font-medium text-fh-text-strong">Page</span>
            <Select
              value={String(pageNumber)}
              onChange={(e) => onPageChange(Number(e.target.value))}
              options={Array.from({ length: Math.max(pageCount, 1) }, (_, i) => ({
                value: String(i + 1),
                label: `${i + 1}`,
              }))}
              className="w-20"
            />
            <span className="text-fh-meta text-fh-text-muted">of {pageCount || "?"}</span>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {types.map(([t, count]) => (
              <button
                key={t}
                type="button"
                onClick={() => setTypeFilter(typeFilter === t ? "" : t)}
                className={cn(
                  "fh-focus cursor-pointer rounded-fh-badge px-2 py-0.5 text-fh-meta font-medium transition",
                  typeFilter === t ? "text-white" : "text-fh-text-mid",
                )}
                style={{
                  backgroundColor: typeFilter === t ? nodeColor(t) : `${nodeColor(t)}1f`,
                }}
              >
                {t} {count}
              </button>
            ))}
          </div>
        </div>

        <div className="relative bg-fh-surface-soft p-4">
          {page && page.pointsWidth && page.pointsHeight ? (
            <div className="relative mx-auto w-full max-w-3xl">
              <img
                src={`data:image/png;base64,${page.base64}`}
                alt={`Page ${pageNumber}`}
                className="block w-full rounded-fh-input shadow-sm"
              />
              {shown.map((n) => {
                const box = bboxToPercent(n.bbox!, page.pointsWidth!, page.pointsHeight!);
                const active = hovered === n.id;
                return (
                  <div
                    key={n.id}
                    onMouseEnter={() => setHovered(n.id)}
                    onMouseLeave={() => setHovered(null)}
                    title={`${n.type}${n.shape ? ` ${n.shape}` : ""}`}
                    className="absolute cursor-default transition-colors"
                    style={{
                      left: `${box.left}%`,
                      top: `${box.top}%`,
                      width: `${box.width}%`,
                      height: `${box.height}%`,
                      outline: `${active ? 2 : 1}px solid ${nodeColor(n.type)}`,
                      backgroundColor: active ? `${nodeColor(n.type)}33` : "transparent",
                    }}
                  />
                );
              })}
            </div>
          ) : (
            <p className="mx-auto max-w-prose py-16 text-center text-fh-body leading-relaxed text-fh-text-muted">
              {page
                ? "This page was restored without its PDF point size, so the boxes cannot be placed. Re-upload the document to see the overlay."
                : "Choose a PDF to see the parse drawn over the page."}
            </p>
          )}
        </div>
      </Card>

      <Card className="flex max-h-[70vh] flex-col overflow-hidden">
        <div className="flex items-center justify-between border-b border-fh-border-soft px-4 py-2.5">
          <span className="text-fh-row font-medium text-fh-text-strong">
            Structure ({shown.length})
          </span>
          {typeFilter && (
            <button
              type="button"
              onClick={() => setTypeFilter("")}
              className="fh-focus cursor-pointer text-fh-meta text-fh-text-secondary hover:text-fh-text-mid"
            >
              clear filter
            </button>
          )}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {shown.map((n) => (
            <div
              key={n.id}
              onMouseEnter={() => setHovered(n.id)}
              onMouseLeave={() => setHovered(null)}
              className={cn(
                "border-b border-fh-border-soft px-4 py-2 transition-colors",
                hovered === n.id ? "bg-fh-surface-soft" : "",
              )}
              // Indent by tree depth: a cell inside a row inside a table is the
              // single most useful thing to see at a glance on a form.
              style={{ paddingLeft: `${16 + n.depth * 10}px` }}
            >
              <div className="flex items-center gap-2">
                <span
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: nodeColor(n.type) }}
                />
                <span className="text-fh-meta font-medium text-fh-text-mid">{n.type}</span>
                {n.shape && <PillBadge value={n.shape} tone={BADGE_TONE.neutral} />}
                {n.tag && <span className="text-fh-meta text-fh-text-muted">{n.tag}</span>}
              </div>
              {n.text && (
                <p className="mt-0.5 line-clamp-3 text-fh-meta leading-relaxed text-fh-text-secondary">
                  {n.text}
                </p>
              )}
            </div>
          ))}
          {!shown.length && (
            <p className="px-4 py-10 text-center text-fh-meta text-fh-text-muted">
              No boxes on this page.
            </p>
          )}
        </div>
      </Card>
    </div>
  );
}
